import {
	useState, useRef, useEffect, RefObject, useCallback,
} from 'react';
import { templateModuleActions } from '../../entities/template-module';
import { useAppDispatch } from '../libs/redux';

interface IProps {
	scrollRef?: RefObject<HTMLElement>; // ref не обязательный
}

/**
 * Хук для подсчета и обновления позиции всплывающего окна для создания тега и позиции модального окна со списком тегов
 * группы.
 * @param scrollRef - ref на элемент, в котором будет происходить скролл.
 */
export const useTagGroupHandlers = ({ scrollRef }: IProps = {}) => {
	const [openGroupId, setOpenGroupId] = useState<string>();
	const [dropdownPosition, setDropdownPosition] = useState<{ top: string; left: string; width: string } | null>(null);
	const [isDropdownVisible, setIsDropdownVisible] = useState(true);
	const groupRefs = useRef<(HTMLDivElement | null)[]>([]);
	const dispatch = useAppDispatch();

	const updateDropdownPosition = useCallback((index: number) => {
		const groupRef = groupRefs.current[index];
		if (!groupRef) return;

		const rect = groupRef.getBoundingClientRect();
		setDropdownPosition({
			top: `${rect.bottom}px`,
			left: `${rect.left}px`,
			width: `${rect.width}px`,
		});

		// Если scrollRef передан, используем его границы для проверки видимости
		if (scrollRef?.current) {
			const containerRect = scrollRef.current.getBoundingClientRect();
			const isVisible = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;
			setIsDropdownVisible(isVisible);
		} else {
			// Если scrollRef не передан, проверяем относительно окна браузера
			const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
			setIsDropdownVisible(isVisible);
		}
	}, [scrollRef]);

	const onGroupClick = (id: string, index: number) => {
		if (openGroupId === id) {
			setOpenGroupId(undefined);
			setDropdownPosition(null);
			return;
		}

		dispatch(templateModuleActions.setIsTagsDropdownOpen(true));
		setOpenGroupId(id);
		updateDropdownPosition(index);
	};

	const resetGroupState = () => {
		setOpenGroupId(undefined);
		setDropdownPosition(null);
		setIsDropdownVisible(true);
	};

	// Подписываемся на событие scroll: если scrollRef передан, то на него, иначе на window.
	useEffect(() => {
		if (!openGroupId) return;

		const handleScroll = () => {
			const activeIndex = groupRefs.current.findIndex((ref) => ref?.dataset.id === openGroupId);
			if (activeIndex !== -1) {
				updateDropdownPosition(activeIndex);
			}
		};

		const scrollElement = scrollRef?.current || window;
		scrollElement.addEventListener('scroll', handleScroll);
		return () => {
			scrollElement.removeEventListener('scroll', handleScroll);
		};
	}, [openGroupId, scrollRef, updateDropdownPosition]);

	return {
		openGroupId,
		// Если dropdown не виден, возвращаем null
		dropdownPosition: isDropdownVisible ? dropdownPosition : null,
		groupRefs,
		onGroupClick,
		resetGroupState,
		setOpenGroupId,
	};
};
